import React, { useState } from 'react';
import { useBlockList } from '../../hooks/useBlockList';
import { useToast } from '../../hooks/useToast';

interface Props {
  userId: string;
  userNick: string;
  myId: string;
  onClose: () => void;
}

const REPORT_REASONS = [
  'Spam or advertising',
  'Harassment or bullying',
  'Inappropriate content',
  'Impersonation',
  'Other',
];

export function BlockReportSheet({ userId, userNick, myId, onClose }: Props) {
  const { blockUser, isBlocked, unblockUser, reportUser } = useBlockList(myId);
  const { show } = useToast();
  const [reportReason, setReportReason] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);
  const blocked = isBlocked(userId);

  const handleBlock = () => {
    if (blocked) {
      unblockUser(userId);
      show(`Unblocked ${userNick}`, 'info');
    } else {
      blockUser(userId);
      show(`Blocked ${userNick}`, 'info');
    }
    onClose();
  };

  const handleReport = () => {
    if (!reportReason) return;
    reportUser(userId, reportReason);
    show('Report submitted. Thank you.', 'success');
    onClose();
  };

  if (showReport) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="text-stabeon-text font-bold text-lg">Report {userNick}</h3>
          <p className="text-stabeon-muted text-sm mt-1">Select a reason:</p>
        </div>
        <div className="flex flex-col gap-2">
          {REPORT_REASONS.map(r => (
            <button
              key={r}
              onClick={() => setReportReason(r)}
              className={`text-left px-4 py-3 rounded-xl text-sm transition-all ${
                reportReason === r ? 'pill-active' : 'card hover:border-stabeon-accent/40'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowReport(false)} className="btn-ghost flex-1">Back</button>
          <button onClick={handleReport} disabled={!reportReason} className="btn-danger flex-1">
            Submit report
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-stabeon-text font-bold text-lg">{userNick}</h3>
      <button
        onClick={handleBlock}
        className={`text-left px-4 py-3 rounded-xl text-sm transition-all ${blocked ? 'card-glow' : 'card hover:border-stabeon-accent/40'}`}
      >
        <span className="font-medium">{blocked ? '✓ Unblock user' : '🚫 Block user'}</span>
        <p className="text-stabeon-muted text-xs mt-0.5">
          {blocked ? 'You will see their content again' : 'Their content will be hidden from you'}
        </p>
      </button>
      <button
        onClick={() => setShowReport(true)}
        className="text-left px-4 py-3 rounded-xl text-sm card hover:border-stabeon-danger/40"
      >
        <span className="font-medium text-stabeon-danger">⚠️ Report user</span>
        <p className="text-stabeon-muted text-xs mt-0.5">Report inappropriate behavior</p>
      </button>
      <button onClick={onClose} className="btn-ghost mt-1">Cancel</button>
    </div>
  );
}
